/**
 * Ambient context stamped onto every `pwa_logs` row.
 *
 * Two kinds of value live here:
 *
 *   STATIC   resolved once at module init from build-time env vars — `tenant`,
 *            `env`, `buildTag`. These cannot change at runtime.
 *   AMBIENT  read at log time — `phase`, `route`, `scanId`, `widgetSessionId`.
 *            These change as the user moves through the app.
 *
 * The ambient values are deliberately PULLED at log time rather than pushed by
 * every call site. `route` and `scanId` already have a single source of truth
 * (`window.location` and the app's own `localStorage.scanID`), so pulling them
 * means the 243 existing log call sites inherit correct context with zero
 * changes — which is the whole premise of Stage 1 (PLAN.md §10).
 *
 * `phase` is the exception: nothing in the app tracks it yet, so it is a
 * settable module value that the Stage-2 phase model owns. Until Stage 2 lands
 * it is `null` on every row, which is honest.
 */

import { BUILD_TAG } from "@/generated/get-build-tag";
import { getAvatarSharingData } from "@/Utils/avatarSharingStorage";
import { Tenants, type Phase, type Tenant } from "./events";

/* ------------------------------------------------------------------ */
/* Static — build-time                                                 */
/* ------------------------------------------------------------------ */

function readFlag(name: string): boolean {
  return process.env[name] === "true";
}

/**
 * Tenant is a build-time property (each target is built with its own env file),
 * EXCEPT that `ttf-pwa-dev` and `ttf-pwa-test` share one env shape and one
 * Firebase project. Hostname is the only thing that separates them, so the
 * non-prod branch falls back to it.
 */
function resolveTenant(): Tenant {
  if (readFlag("REACT_APP_IS_MALCHUS_ENV")) return Tenants.MALCHUS;
  if (readFlag("REACT_APP_IS_NEEMS_ENV")) return Tenants.NEEMS;
  if (readFlag("REACT_APP_IS_SISLEY_ENV")) return Tenants.SISLEY;
  if (readFlag("REACT_APP_IS_STYLEMATCH_ENV")) return Tenants.STYLEMATCH;
  if (readFlag("REACT_APP_IS_PROD_ENV")) return Tenants.MAIN;

  try {
    if (window.location.hostname.indexOf("ttf-pwa-test") !== -1) {
      return Tenants.TEST;
    }
  } catch {
    /* ignore */
  }
  return Tenants.DEV;
}

const tenant: Tenant = resolveTenant();
const env: "prod" | "dev" = readFlag("REACT_APP_IS_PROD_ENV") ? "prod" : "dev";

/* ------------------------------------------------------------------ */
/* Ambient — runtime                                                   */
/* ------------------------------------------------------------------ */

let currentPhase: Phase | null = null;

/**
 * Set by the Stage-2 phase model. Left `null` before then.
 *
 * Kept as plain module state rather than a Jotai atom on purpose: the logger is
 * called from singletons and workers-adjacent code that live outside React, and
 * reading a store from there would couple telemetry to render timing.
 */
export function setCurrentPhase(phase: Phase | null): void {
  currentPhase = phase;
}

export function getCurrentPhase(): Phase | null {
  return currentPhase;
}

/**
 * Looks like an opaque id rather than a route word: long, or mixed
 * letters+digits. Every route in `App.tsx` is currently static, so this never
 * fires today — it exists so a future `/foo/:id` route cannot silently make
 * `route` a high-cardinality indexed field (PLAN.md §4.4).
 */
function looksLikeId(segment: string): boolean {
  if (segment.length > 24) return true;
  return /\d/.test(segment) && /[a-z]/i.test(segment) && segment.length > 12;
}

function normalizeRoute(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "") || "/";
  const parts = trimmed.split("/");
  for (let i = 0; i < parts.length; i++) {
    if (parts[i] && looksLikeId(parts[i])) parts[i] = ":id";
  }
  return parts.join("/").slice(0, 80).toLowerCase() || "/";
}

/** Current route, normalized. Never includes the query string — it can carry PII. */
export function getRoute(): string {
  try {
    return normalizeRoute(window.location.pathname);
  } catch {
    return "unknown";
  }
}

/**
 * The in-flight scan id, read from the app's own source of truth.
 *
 * `localStorage.scanID` outlives the `ScanUploader` and is only cleared by
 * `reset()` or `scanRecovery` — see HERE_BE_DRAGONS.md. That staleness is a
 * feature here: a row written after a reload still carries the scan the journey
 * is about, which is exactly what an investigator wants.
 */
export function getScanId(): string | null {
  try {
    return window.localStorage.getItem("scanID") || null;
  } catch {
    return null;
  }
}

/** The `widget_sessions/{id}` join key, when this session came from a store deeplink. */
export function getWidgetSessionId(): string | null {
  try {
    const { sessionId } = getAvatarSharingData();
    return sessionId || null;
  } catch {
    return null;
  }
}

export function getTenant(): Tenant {
  return tenant;
}

export function getEnv(): "prod" | "dev" {
  return env;
}

export function getBuildTag(): string {
  return BUILD_TAG;
}
